import DateRangePicker from "@/components/forms/DateRangePicker/DateRangePicker";
import { SellerSalesDetailSection, SellerSalesSnapshot } from "@boxem/core-api-schema";
import { useState } from "react";
import style from "./sales-snapshot-card.module.css";
import IconClose from "@/components/icons/IconClose";
import dayjs from "dayjs";
import { FormatValueOptions, formatValue } from "@/utils/formatUtils";
import { DateRangeValue, isSameDay } from "@/utils/dateUtils";
import StatCard from "@/components/StatCard/StatCard";

interface SnapshotDetailSectionProps {
  detailSection: SellerSalesDetailSection;
  isFeatured?: boolean;
  title: string;
  formatAs?: FormatValueOptions;
}

export const SnapshotDetailSection = ({ title, detailSection, isFeatured, formatAs = 'number' }: SnapshotDetailSectionProps) => {
  const [isOpened, setIsOpened] = useState(false);
  const hasAccordion = detailSection?.data && Object.keys(detailSection.data).length > 0;
  // Define the mapping for Amazon fees
  const amazonFeesMapping = {
    // Main category
    'amazonFees': 'Amazon Fees',
    
    // Individual fee types
    'referralFees': 'Referral Fees',
    'fbaPerUnitFullfillmentFees': 'FBA Per Unit Fulfillment Fees',
    'otherServiceFees': 'Other Service Fees',
    'FBAInboundConvenienceFee': 'FBA Inbound Convenience Fee',
    'FBAInboundTransportationFee': 'FBA Inbound Transportation Fee',
    'FBARemovalFee': 'FBA Removal Fee',
    
    // Additional common Amazon fee types (for completeness)
    'storageFees': 'Storage Fees',
    'longTermStorageFees': 'Long Term Storage Fees',
    'inventoryPlacementFees': 'Inventory Placement Fees',
    'refundProcessingFees': 'Refund Processing Fees',
    'returnProcessingFees': 'Return Processing Fees',
    'disposalFees': 'Disposal Fees',
    'liquidationFees': 'Liquidation Fees',
    'highVolumeListingFees': 'High Volume Listing Fees',
    'subscriptionFees': 'Subscription Fees',
    'advertisingFees': 'Advertising Fees',
    'promotionalFees': 'Promotional Fees'
  };

  // Function to get the mapped display name or use original if mapping doesn't exist
  const getDisplayName = (key: string) => {
    // Only use mapping for Amazon fees section
    if (title === "Amazon Fees" && amazonFeesMapping[key]) {
      return amazonFeesMapping[key];
    }
    return key;
  };

  return (
    detailSection && <div>
      <button
        onClick={() => hasAccordion && setIsOpened(!isOpened)}
        className={`
          ${style.snapshotCardDetailSectionButton}
          ${isFeatured ? style.featuredSnapshotCardDetailSectionButton : ""}
        `}
      >
        {hasAccordion && <svg xmlns="http://www.w3.org/2000/svg" className={`${style.snapshotCardDetailSectionButtonIcon} ${isOpened ? style.activeSnapshotCardDetailSectionButtonIcon : ""}`} width="5" height="8" viewBox="0 0 5 8" fill="none">
          <path d="M0.183333 7.53345C0.0611109 7.41122 0 7.25567 0 7.06678C0 6.87789 0.0611109 6.72234 0.183333 6.60011L2.78333 4.00011L0.183333 1.40011C0.0611109 1.27789 0 1.12234 0 0.933447C0 0.744558 0.0611109 0.589003 0.183333 0.46678C0.305555 0.344558 0.461111 0.283447 0.65 0.283447C0.838889 0.283447 0.994445 0.344558 1.11667 0.46678L4.18333 3.53345C4.25 3.60011 4.29733 3.67234 4.32533 3.75011C4.35289 3.82789 4.36667 3.91123 4.36667 4.00011C4.36667 4.089 4.35289 4.17234 4.32533 4.25011C4.29733 4.32789 4.25 4.40011 4.18333 4.46678L1.11667 7.53345C0.994445 7.65567 0.838889 7.71678 0.65 7.71678C0.461111 7.71678 0.305555 7.65567 0.183333 7.53345Z" fill="currentColor" />
        </svg>}
        <span>{title}</span>
        <span className={style.snapshotCardDetailSectionButtonValue}>{formatValue(detailSection.total, formatAs)}</span>
      </button>
      {hasAccordion && isOpened && <StatCard.List className={style.snapshotCardDetailSectionList}>
        {isOpened && Object.entries(detailSection.data).map((item, index) => (
          <StatCard.ListItem 
            className={style.snapshotCardDetailListItem} 
            key={index} 
            label={getDisplayName(item[0])} 
            value={item[1]} 
            formatAs={formatAs} 
          />))}
      </StatCard.List>}
    </div>
  )
}

interface SnapshotDetailFooterItemProps {
  detailSection: SellerSalesDetailSection;
  title: string;
  formatAs?: FormatValueOptions;
}
export const SnapshotDetailFooterItem = ({ title, detailSection, formatAs = 'number' }: SnapshotDetailFooterItemProps) => {
  return (
    detailSection && <StatCard.ListItem className={style.snapshotCardDetailFooterItem} label={title} value={detailSection.total} formatAs={formatAs} />
  )
}


export interface SalesSnapshotCardProps {
  activeSnapshotIndex: number;
  cardIndex: number;
  isLoading?: boolean;
  defaultInterval?: DateRangeValue;
  onChangeInterval?: (cardIndex: number, dateRange: DateRangeValue) => void;
  onClick?: (cardIndex: number, dateRange: DateRangeValue) => void;
  data?: SellerSalesSnapshot;
}

export default function SalesSnapshotCard({
  activeSnapshotIndex,
  cardIndex,
  defaultInterval,
  data,
  isLoading,
  onChangeInterval,
  onClick,
}: SalesSnapshotCardProps) {
  const [selectedInterval, setSelectedInterval] = useState<DateRangeValue | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);

  const handleDateRangePickerChange = (dateRange: DateRangeValue) => {
    setSelectedInterval(dateRange)
    onChangeInterval?.(cardIndex, dateRange);
  }

  const handleClick = () => {
    onClick?.(cardIndex, selectedInterval || defaultInterval);
  }

  const displayedSnapshotLabel = selectedInterval?.label || defaultInterval?.label || "Sales Snapshot";
  const displayedDateRange = selectedInterval || defaultInterval;
  const dateLabel = [dayjs(displayedDateRange.from).format('MMM D'), displayedDateRange.to && !isSameDay(displayedDateRange.from, displayedDateRange.to) && dayjs(displayedDateRange.to).format('MMM D')].filter(Boolean).join(' - ');

  const showLoading = !data || isLoading;
  const salesSnapshot = data?.data;
  const salesDetail = salesSnapshot?.detail;
  return (
    <div className={style.snapshotCardWrapper}>
      <StatCard className={`${style.snapshotCard} ${activeSnapshotIndex === cardIndex ? style.activeSnapshotCard : ""}`} onClick={handleClick}>
        <StatCard.Header label={displayedSnapshotLabel} className={style.snapshotCardHeader}>
          {salesDetail && <button className={style.snapshotCardDetailToggle} onClick={() => setIsDetailOpen(!isDetailOpen)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
              <path d="M5.57344 6.55C5.70677 6.55 5.83577 6.525 5.96044 6.475C6.08577 6.425 6.1901 6.35833 6.27344 6.275L10.8734 1.675C11.0568 1.49167 11.1484 1.25833 11.1484 0.974999C11.1484 0.691666 11.0568 0.458333 10.8734 0.275C10.6901 0.0916663 10.4568 0 10.1734 0C9.8901 0 9.65677 0.0916663 9.47344 0.275L5.57344 4.175L1.67344 0.275C1.4901 0.0916663 1.25677 0 0.973437 0C0.690104 0 0.456772 0.0916663 0.273438 0.275C0.0901051 0.458333 -0.00156307 0.691666 -0.00156307 0.974999C-0.00156307 1.25833 0.0901051 1.49167 0.273438 1.675L4.87344 6.275C4.97344 6.375 5.08177 6.44567 5.19844 6.487C5.3151 6.529 5.4401 6.55 5.57344 6.55Z" fill="currentColor" />
            </svg>
          </button>}
          <DateRangePicker defaultValue={defaultInterval} onSelectDateRange={handleDateRangePickerChange} />
          <StatCard.Data value={salesSnapshot?.totalSales || 0} formatAs="currency" isLoading={showLoading} />
          <svg xmlns="http://www.w3.org/2000/svg" width="168" height="115" viewBox="0 0 168 115" fill="none" className={style.snapshotCardBackground}>
            <path d="M9.15333 66.2771L0.5 115H156.5C163.127 115 168.5 109.627 168.5 103V8.18171C168.5 0.675064 158.438 -1.80212 154.953 4.84661C153.364 7.87785 149.858 9.3675 146.573 8.40666L140.169 6.53353C131.819 4.09093 123.112 9.07968 120.997 17.5188L116.266 36.391C113.08 49.0999 96.8051 52.8017 88.4358 42.721L86.3211 40.1738C82.2906 35.3193 75.2982 34.179 69.9343 37.5017L69.06 38.0433C64.415 40.9206 58.3814 40.097 54.6775 36.08C49.4158 30.3736 40.1138 31.4805 36.3382 38.2623L32.7098 44.7796C30.9549 47.9318 28.1862 50.3983 24.8531 51.7789L18.7839 54.2929C13.739 56.3825 10.1082 60.9007 9.15333 66.2771Z" fill="currentColor" />
          </svg>
        </StatCard.Header>
        <StatCard.Content className={style.snapshotCardContent}>
          <StatCard.List>
            <StatCard.ListItem label="Orders / Units" value={`${salesSnapshot?.orderCount || 0} / ${salesSnapshot?.unitCount || 0}`} isLoading={showLoading} />
            <StatCard.ListItem label="Refunds" value={salesSnapshot?.refundCount || 0} formatAs="number" isLoading={showLoading} />
            <StatCard.ListItem label="Estimated Payout" value={salesSnapshot?.estimatedPayout || 0} formatAs="currency" isLoading={showLoading} />
            <StatCard.ListItem label="Gross profit" value={salesSnapshot?.netProfit || 0} formatAs="currency" isLoading={showLoading} />
          </StatCard.List>
        </StatCard.Content>
      </StatCard>
      {isDetailOpen && salesDetail && (
        <StatCard className={style.snapshotCardDetail}>
          <button className={style.snapshotCardDetailCloseButton} onClick={() => setIsDetailOpen(!isDetailOpen)}>
            <IconClose />
          </button>
          <StatCard.Header label={displayedSnapshotLabel} className={style.snapshotCardDetailHeader}>
            <div className={style.snapshotCardDetailDate}>{dateLabel}</div>
          </StatCard.Header>
          <StatCard.Content className={style.snapshotCardDetailContent}>
            <SnapshotDetailSection detailSection={salesDetail.sales} title="Sales" isFeatured formatAs="currency" />
            <SnapshotDetailSection detailSection={salesDetail.units} title="Units" />
            <SnapshotDetailSection detailSection={salesDetail.promotions} title="Promotions" formatAs="currency" />
            <SnapshotDetailSection detailSection={salesDetail.shippingCosts} title="Shipping Costs" formatAs="currency" />
            <SnapshotDetailSection detailSection={salesDetail.refundCosts} title="Refund Costs" formatAs="currency" />
            <SnapshotDetailSection detailSection={salesDetail.amazonFees} title="Amazon Fees" formatAs="currency" />
            <SnapshotDetailSection detailSection={salesDetail.costOfGoods} title="Cost of Goods" formatAs="currency" />
            <SnapshotDetailSection detailSection={salesDetail.estimatedPayout} title="Estimated Payout" formatAs="currency" />
            <SnapshotDetailSection detailSection={salesDetail.grossProfit} title="Gross Profit" isFeatured formatAs="currency" />
          </StatCard.Content>
          <StatCard.Content className={style.snapshotCardDetailFooter}>
            <StatCard.List>
              <SnapshotDetailFooterItem detailSection={salesDetail.percentageRefunds} title="% Refunds" formatAs="percent" />
              {/* <SnapshotDetailFooterItem detailSection={salesDetail.sellableReturns} title="Sellable Returns" formatAs="percent" /> */}
              <SnapshotDetailFooterItem detailSection={salesDetail.margin} title="Margin" formatAs="percent" />
              <SnapshotDetailFooterItem detailSection={salesDetail.roi} title="ROI" formatAs="percent" />
            </StatCard.List>
          </StatCard.Content>
        </StatCard>)}
    </div>
  );
}
